import React, {Component} from 'react'
import propTypes from 'prop-types'
import DayPicker from 'react-day-picker'
import Popover from '@material-ui/core/Popover'
import MenuList from '@material-ui/core/MenuList'
import MenuItem from '@material-ui/core/MenuItem'
import Button from 'components/button'
import {isNil} from 'lodash'
import {prefix} from 'helpers/component'
import themeVars from 'theme/themeVars'
import _Date from 'containers/date'
import {customDateRange} from 'helpers/dateRangeSelectors'
import {datePickerRanges} from 'helpers/dateRange'
import {ONE_DAY_MILLISECONDS, roundDaysDown, roundDaysUp} from 'helpers/dates'
import {getDateRangeControlForDatePickerRange, getDateRangeForKey} from 'helpers/dateRange'
import './datePickerRange.scss'

export const updateDateRange = (currentFrom, currentTo, newDay, selectingRangeStart) => {
  const from = selectingRangeStart ? newDay : currentFrom
  const to = newDay

  return {
    from: roundDaysDown(from > to ? to : from),
    to: roundDaysUp(from > to ? new Date(from.getTime() + 1) : to),
  }
}

export default class DatePickerRange extends Component {

  constructor(props) {
    super(props)

    this.state = this.getDefaultState()
    this.getClass = prefix('datePickerRange-')
    this.numberOfMonths = 2
  }

  componentDidUpdate(prevProps) {
    const {open, startDate, endDate, timeKey} = this.props
    if (
      (open && !prevProps.open) ||
      prevProps.startDate !== startDate ||
      prevProps.endDate !== endDate ||
      prevProps.timeKey !== timeKey
    ) {
      this.setState(this.getDefaultState())
    }
  }

  getDefaultState = () => {
    const {startDate, endDate, timeKey, invoiceFinancialYearStart} = this.props

    if (timeKey) {
      const {startDate: from, endDate: to} = getDateRangeForKey(timeKey, invoiceFinancialYearStart, true)

      return {
        timeKey,
        from,
        to,
        selectingRangeStart: true,
      }

    } else {
      return {
        timeKey: null,
        from: startDate ? new Date(startDate) : null,
        to: endDate ? new Date(endDate) : null,
        selectingRangeStart: true,
      }
    }
  }

  onDayClick = (day) => {
    const {from, to, selectingRangeStart} = this.state
    this.setState({
      ...updateDateRange(from, to, day, selectingRangeStart),
      selectingRangeStart: !selectingRangeStart,
      timeKey: null,
    })
  }

  onPresetSelect = ({value}) => {
    const {invoiceFinancialYearStart} = this.props
    const {timeKey, from, to} = this.state

    if (value === customDateRange.value) {
      const isCustomDateRange = timeKey === null
      this.setState({
        from: isCustomDateRange ? from : null,
        to: isCustomDateRange ? to : null,
        timeKey: null,
      })

    } else {
      const {startDate, endDate} = getDateRangeForKey(value, invoiceFinancialYearStart, true)
      this.setState({
        from: startDate,
        to: endDate,
        timeKey: value,
      })
    }
  }

  onSubmit = () => {
    const {onSubmit} = this.props
    const {from, to, timeKey} = this.state
    onSubmit({
      startDate: from ? from.getTime() : null,
      endDate: to ? to.getTime() : null,
      timeKey,
    })
    this.onClose()
  }

  onClose = () => {
    const {onClose} = this.props
    this.setState(this.getDefaultState(), () => onClose && onClose())
  }

  getMenuItemStyle = (option) => {
    const {timeKey} = this.state
    const {value} = option

    const selectedStyle = {
      fontWeight: 'bold',
      color: themeVars.palette.primary1Color,
    }

    if (value === customDateRange.value && timeKey === null) {
      return selectedStyle
    }

    if (timeKey === value) {
      return selectedStyle
    }

    return null
  }

  getMenuItem = (option) => {
    return (
      <MenuItem
        key={option.value}
        style={this.getMenuItemStyle(option)}
        onClick={() => this.onPresetSelect(option)}
      >
        {option.label}
      </MenuItem>
    )
  }

  getMenuItems = () => {
    const {presetTypes} = this.props

    const options = []
    options.push(customDateRange)
    options.push(...getDateRangeControlForDatePickerRange(presetTypes))

    return options.map(this.getMenuItem)
  }

  getDateRangeString = () => {
    const {from, to} = this.state
    const day = (!from && !to) ? 'first' : 'last'
    const startDate = (from && to) ? <_Date date={from}/> : null
    const endDate = (from && to) ? <_Date date={this.getUIToDate(to)}/> : null
    return (
      <p>
        {startDate && endDate &&
          <>
            <span className={'startDate'}>{startDate}</span>
            <span className={'endDate'}>{endDate}</span>
          </>
        }
        {day && !startDate && `Please select ${day} day`}
      </p>
    )
  }

  getUIToDate = (storedToDate) => {
    if (isNil(storedToDate)) return null
    return new Date(storedToDate.getTime() - ONE_DAY_MILLISECONDS)
  }

  render() {
    const {open, anchor, anchorOrigin, targetOrigin, showPresets, onClose} = this.props
    const {from} = this.state
    const to = this.getUIToDate(this.state.to)
    const modifiers = {start: from, end: to}
    const menuItems = this.getMenuItems()

    return (
      <Popover
        classes={{paper: this.getClass('popover')}}
        anchorEl={anchor}
        anchorOrigin={anchorOrigin}
        transformOrigin={targetOrigin}
        open={open}
        onClose={onClose}
        onClick={(e) => e.stopPropagation()}
        disableRestoreFocus={true}
      >
        <div className={this.getClass('container')}>
          {showPresets && (
            <div
              className={
                this.getClass([
                  'pre-selectors',
                  menuItems.length > 9 ? 'pre-selectors-small' : 'pre-selectors-large',
                ])
              }
            >
              <div className={this.getClass('pre-selectors-outer')}>
                <MenuList>
                  {menuItems}
                </MenuList>
              </div>
            </div>
          )}
          <div className={'calender-container'}>
            <div className={'header'}>
              {this.getDateRangeString()}
            </div>
            <DayPicker
              className={'selectable'}
              numberOfMonths={this.numberOfMonths}
              selectedDays={[from, {from, to}]}
              modifiers={modifiers}
              month={from}
              onDayClick={this.onDayClick}
            />
            <div className={'selection'}>
              {onClose && (
                <Button flat={true}
                        className={'cancel-date-picker-range'}
                        label={'cancel'}
                        onClick={this.onClose}
                />
              )}
              <Button
                className={'ok-date-picker-range'}
                disabled={!from || !to}
                onClick={this.onSubmit}
                label={'ok'}
                primary={true}
              />
            </div>
          </div>
        </div>
      </Popover>
    )
  }
}

DatePickerRange.propTypes = {
  open: propTypes.bool,
  anchor: propTypes.object,
  startDate: propTypes.number,
  endDate: propTypes.number,
  timeKey: propTypes.string,
  showPresets: propTypes.bool,
  presetTypes: propTypes.oneOf(Object.keys(datePickerRanges)),
  anchorOrigin: propTypes.shape({
    horizontal: propTypes.string.isRequired,
    vertical: propTypes.string.isRequired,
  }),
  targetOrigin: propTypes.shape({
    horizontal: propTypes.string.isRequired,
    vertical: propTypes.string.isRequired,
  }),
  onClose: propTypes.func,
  onSubmit: propTypes.func.isRequired,

  // Provided by connect
  invoiceFinancialYearStart: propTypes.number,
}

DatePickerRange.defaultProps = {
  open: false,
  showPresets: true,
  anchorOrigin: {
    'horizontal': 'left',
    'vertical': 'top',
  },
  targetOrigin: {
    'horizontal': 'left',
    'vertical': 'top',
  },
}
